import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { PriceDisplay } from '@markku/components'
import {
  INVOICE_FEES,
  INVOICING_FEE_START_DATE,
  type VisitWithRelations,
} from '@markku/types'
import {
  calculateFinalPrice,
  formatDate,
  formatDateTime,
  partition,
  sortBy,
  sum,
} from '@markku/utils'
import {
  IconAlertTriangle,
  IconCheck,
  IconFileInvoice,
  IconInfoCircle,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { Trans, useLingui } from '@lingui/react/macro'
import { billingHooks } from '../../../lib/trpcHooks'
import { InvoiceItem } from './InvoiceItem'

interface VisitPaymentCardProps {
  visit: VisitWithRelations
}

export function VisitPaymentCard({ visit }: VisitPaymentCardProps) {
  const { t } = useLingui()
  const { mutate: createCheckout, isPending } =
    billingHooks.useCreateVisitPaymentCheckout()

  // Get all invoices sorted by createdAt descending (most recent first)
  const sortedInvoices = sortBy(visit.invoices || [], [
    (inv) => inv.createdAt,
    'desc',
  ])
  const hasInvoices = sortedInvoices.length > 0

  // Only show for visits with separately invoiced tasks that have a billable amount
  const billableTasks =
    visit.visitTasks?.filter(
      (task) =>
        task.billingType === 'separately_invoiced' &&
        calculateFinalPrice(task.price, task.discountPercent) > 0,
    ) || []

  // Don't show if no billable tasks AND no invoices
  if (!billableTasks.length && !hasInvoices) {
    return null
  }

  const hasSubscription = !!visit.subscription

  // Check feature flag for one-time purchase billing
  const featureEnabled =
    import.meta.env.VITE_ENABLE_ONE_TIME_PURCHASE_BILLING === 'true'

  // Show card if:
  // 1. Subscriber with auto-billed tasks (always show, feature flag doesn't apply)
  // 2. Non-subscriber with separately-invoiced tasks AND feature flag enabled
  if (!hasSubscription && !featureEnabled) {
    return null
  }

  // Split tasks into paid, billed (invoiced but not paid), and unbilled
  const [paidTasks, notPaidTasks] = partition(
    billableTasks,
    (task) => !!task.paidAt,
  )
  const [billedTasks, unbilledTasks] = partition(
    notPaidTasks,
    (task) => !!task.billedAt,
  )

  const allPaid = notPaidTasks.length === 0
  const hasBilledTasks = billedTasks.length > 0
  const hasUnbilledTasks = unbilledTasks.length > 0
  const partiallyPaid = paidTasks.length > 0 && notPaidTasks.length > 0

  // Calculate totals
  const paidTotal = sum(
    paidTasks.map((task) =>
      calculateFinalPrice(task.price, task.discountPercent),
    ),
  )
  const unbilledTotal = sum(
    unbilledTasks.map((task) =>
      calculateFinalPrice(task.price, task.discountPercent),
    ),
  )

  const paidDate = paidTasks[0]?.paidAt

  // Calculate 48-hour deadline for non-subscribers (only relevant for unbilled tasks)
  const deadline = visit.completedAt
    ? dayjs(visit.completedAt).add(48, 'hours')
    : null
  const isOverdue = deadline ? dayjs().isAfter(deadline) : false

  // Check if invoicing fee applies (visits created on/after start date)
  const feeApplies = visit.createdAt
    ? new Date(visit.createdAt) >= new Date(INVOICING_FEE_START_DATE)
    : false

  const handlePayNow = () => {
    createCheckout({ visitId: visit.id })
  }

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={4}>
            {hasSubscription ? (
              <Trans>Additional work</Trans>
            ) : (
              <Trans>Payment</Trans>
            )}
          </Title>
          {allPaid && paidDate && (
            <Badge color="green" size="lg">
              <Trans>Paid {formatDate(paidDate)}</Trans>
            </Badge>
          )}
          {partiallyPaid && (
            <Badge color="orange" size="lg">
              <Trans>Partially paid</Trans>
            </Badge>
          )}
        </Group>

        {/* Non-subscriber - invoices (show all, including voided) */}
        {!hasSubscription && hasInvoices && (
          <Stack gap="xs">
            <Text size="sm" fw={500} c="dimmed">
              {sortedInvoices.length > 1 ? (
                <Trans>Invoices</Trans>
              ) : (
                <Trans>Invoice</Trans>
              )}
            </Text>
            {sortedInvoices.map((invoice) => (
              <InvoiceItem key={invoice.id} invoice={invoice} />
            ))}
          </Stack>
        )}

        {/* Subscriber - unpaid tasks */}
        {hasSubscription && notPaidTasks.length > 0 && (
          <Alert
            icon={<IconInfoCircle size={16} />}
            title={t`Additional work will be added to your next monthly invoice automatically`}
            color="blue"
          >
            {partiallyPaid
              ? t`New additional work will be added automatically to your next monthly invoice. You don't need to do anything.`
              : t`The additional work listed below will be added automatically to your next monthly invoice. You don't need to do anything.`}
          </Alert>
        )}

        {/* Non-subscriber payment deadline alert - only for unbilled tasks */}
        {!hasSubscription && hasUnbilledTasks && (
          <Alert
            icon={
              feeApplies ? (
                <IconAlertTriangle size={16} />
              ) : (
                <IconInfoCircle size={16} />
              )
            }
            title={
              isOverdue
                ? t`Payment deadline in app has expired`
                : feeApplies
                  ? t`Pay in app before deadline and save ${INVOICE_FEES.EMAIL}€`
                  : t`Payment deadline`
            }
            color={
              isOverdue
                ? feeApplies
                  ? 'red'
                  : 'blue'
                : feeApplies
                  ? 'orange'
                  : 'blue'
            }
          >
            {!isOverdue && deadline && (
              <Text size="sm" fw={600}>
                <Trans>Deadline:</Trans>{' '}
                {formatDateTime(deadline.toISOString())}
              </Text>
            )}
            <Text size="sm" mt={!isOverdue && deadline ? 'xs' : undefined}>
              {isOverdue
                ? feeApplies
                  ? t`Email invoice will be sent automatically (includes ${INVOICE_FEES.EMAIL}€ invoicing fee). Payment period is 14 days from invoice date.`
                  : t`Email invoice will be sent automatically. Payment period is 14 days from invoice date.`
                : feeApplies
                  ? t`If you don't pay in app by the deadline, we will send an email invoice (includes ${INVOICE_FEES.EMAIL}€ invoicing fee). Invoice payment period is 14 days.`
                  : t`If you don't pay in app by the deadline, we will send an email invoice. Invoice payment period is 14 days.`}
            </Text>
          </Alert>
        )}

        {/* Paid tasks section */}
        {paidTasks.length > 0 && (
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" fw={600} c="dimmed">
                <Trans>Paid work:</Trans>
              </Text>
              {paidDate && (
                <Text size="xs" c="dimmed">
                  {formatDate(paidDate)}
                </Text>
              )}
            </Group>
            {paidTasks.map((task) => (
              <Group key={task.id} justify="space-between" wrap="nowrap">
                <Group gap="xs" style={{ flex: 1 }}>
                  <IconCheck size={16} color="var(--mantine-color-green-6)" />
                  <Text size="sm" c="dimmed">
                    {task.taskName}
                  </Text>
                </Group>
                <PriceDisplay
                  basePrice={task.price}
                  discountPercent={task.discountPercent}
                  size="sm"
                />
              </Group>
            ))}
            {partiallyPaid && (
              <Group justify="space-between" pt="xs">
                <Text size="sm" fw={600} c="dimmed">
                  <Trans>Total paid</Trans>
                </Text>
                <Text size="sm" fw={600} c="dimmed">
                  {paidTotal.toFixed(2)}€
                </Text>
              </Group>
            )}
          </Stack>
        )}

        {/* Billed tasks section (invoiced but not paid) */}
        {billedTasks.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600} c="dimmed">
              <Trans>Invoiced work:</Trans>
            </Text>
            {billedTasks.map((task) => (
              <Group key={task.id} justify="space-between" wrap="nowrap">
                <Group gap="xs" style={{ flex: 1 }}>
                  <IconFileInvoice
                    size={16}
                    color="var(--mantine-color-blue-6)"
                  />
                  <Text size="sm">{task.taskName}</Text>
                </Group>
                <PriceDisplay
                  basePrice={task.price}
                  discountPercent={task.discountPercent}
                  size="sm"
                />
              </Group>
            ))}
          </Stack>
        )}

        {/* Unbilled tasks section (not yet invoiced) */}
        {unbilledTasks.length > 0 && (
          <>
            <Stack gap="xs">
              <Text size="sm" fw={600} c="dimmed">
                {partiallyPaid || hasBilledTasks ? (
                  <Trans>Unpaid work:</Trans>
                ) : (
                  <Trans>Completed work:</Trans>
                )}
              </Text>
              {unbilledTasks.map((task) => (
                <Group key={task.id} justify="space-between" wrap="nowrap">
                  <Text size="sm" style={{ flex: 1 }}>
                    {task.taskName}
                  </Text>
                  <PriceDisplay
                    basePrice={task.price}
                    discountPercent={task.discountPercent}
                    size="sm"
                  />
                </Group>
              ))}
            </Stack>

            <Group
              justify="space-between"
              pt="xs"
              style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}
            >
              <Text size="lg" fw={700}>
                {partiallyPaid || hasBilledTasks ? (
                  <Trans>Amount due</Trans>
                ) : (
                  <Trans>Total</Trans>
                )}
              </Text>
              <Text size="xl" fw={700}>
                {unbilledTotal.toFixed(2)}€
              </Text>
            </Group>

            {/* Payment button for non-subscribers - only for unbilled tasks */}
            {!hasSubscription && (
              <Button
                fullWidth
                size="lg"
                disabled={isOverdue || isPending}
                onClick={handlePayNow}
                loading={isPending}
              >
                <Trans>Pay now</Trans>
              </Button>
            )}
          </>
        )}
      </Stack>
    </Card>
  )
}
